import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  DpEmployeeContract,
  DpProbationChange,
  listProbationChangesApi,
  updateProbationApi,
} from "../api/contractsApi";
import { formatDateOnly } from "../utils/format";
import { addDaysIso, probation45Plus45 } from "../utils/probation";

type Props = {
  token: string;
  contract: DpEmployeeContract;
  onClose: () => void;
  onSaved: () => void;
};

const range = (first: string | null, end: string | null) => {
  if (!end) return "sem experiência";
  return first
    ? `1º período até ${formatDateOnly(first)} e fim em ${formatDateOnly(end)}`
    : `fim em ${formatDateOnly(end)}`;
};

/**
 * Edição das datas da experiência de um contrato vigente. Motivo obrigatório; cada
 * alteração fica no histórico (o backend não reescreve, só acrescenta).
 */
export default function ProbationEditModal({ token, contract, onClose, onSaved }: Props) {
  const admission = contract.admissionDate.slice(0, 10);
  const [form, setForm] = useState({
    probationFirstEndDate: contract.probationFirstEndDate?.slice(0, 10) ?? "",
    probationEndDate: contract.probationEndDate?.slice(0, 10) ?? "",
    reason: "",
  });
  const [changes, setChanges] = useState<DpProbationChange[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listProbationChangesApi(token, contract.id)
      .then(setChanges)
      .catch(() => setChanges([]));
  }, [token, contract.id]);

  const save = async () => {
    if (form.reason.trim().length < 3) return toast.error("Informe o motivo da alteração.");
    if (form.probationFirstEndDate && !form.probationEndDate) {
      return toast.error("Informe o fim da experiência junto do fim do 1º período.");
    }
    setSaving(true);
    try {
      await updateProbationApi(token, contract.id, {
        probationFirstEndDate: form.probationFirstEndDate || null,
        probationEndDate: form.probationEndDate || null,
        reason: form.reason.trim(),
      });
      toast.success("Experiência atualizada.");
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao alterar a experiência.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Contrato de experiência</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>

        <div className="dpModal__body">
          <p className="dpNotice">
            Admissão em {formatDateOnly(admission)}. A experiência vai até 90 dias contando a
            admissão (até {formatDateOnly(addDaysIso(admission, 89))}) e pode ser prorrogada uma
            vez. Deixe as duas datas vazias se o contrato não for de experiência.
          </p>

          <div className="dpFormGrid">
            <div className="dpFormGroup">
              <label htmlFor="probFirst">Fim do 1º período</label>
              <input
                id="probFirst"
                type="date"
                className="dpInput"
                value={form.probationFirstEndDate}
                min={addDaysIso(admission, 1)}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, probationFirstEndDate: e.target.value }))}
              />
              <span className="dpFormHint">Vazio = experiência num período só.</span>
            </div>

            <div className="dpFormGroup">
              <label htmlFor="probEnd">Fim da experiência</label>
              <input
                id="probEnd"
                type="date"
                className="dpInput"
                value={form.probationEndDate}
                min={addDaysIso(admission, 1)}
                max={addDaysIso(admission, 89)}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, probationEndDate: e.target.value }))}
              />
            </div>
          </div>

          <button
            type="button"
            className="dpBtn dpBtn--secondary"
            disabled={saving}
            onClick={() => setForm((f) => ({ ...f, ...probation45Plus45(admission) }))}
          >
            Preencher 45 + 45
          </button>

          <div className="dpFormGroup" style={{ marginTop: 16 }}>
            <label htmlFor="probReason">Motivo *</label>
            <textarea
              id="probReason"
              className="dpTextarea"
              maxLength={500}
              value={form.reason}
              disabled={saving}
              placeholder="Ex.: datas da planilha de período probatório."
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>

          {changes && changes.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <strong>Alterações anteriores</strong>
              <ul className="dpRuleList" style={{ marginTop: 8 }}>
                {changes.map((change) => (
                  <li key={change.id}>
                    {new Date(change.createdAt).toLocaleDateString("pt-BR")}, por{" "}
                    {change.createdBy.nomeCompleto}: antes, {range(change.previousFirstEndDate, change.previousEndDate)}; depois,{" "}
                    {range(change.newFirstEndDate, change.newEndDate)}. Motivo: {change.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={save} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </footer>
      </div>
    </div>
  );
}
